import { useEffect, useState } from "react";
import { ArrowLeft, Share, Plus, MoreVertical, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FaApple, FaAndroid } from "react-icons/fa";
import { PageTransition } from "@/components/PageTransition";

const Install = () => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [platform, setPlatform] = useState<"ios" | "android" | "desktop">("desktop");

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) {
      setPlatform("ios");
    } else if (/android/.test(ua)) {
      setPlatform("android");
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setIsInstalled(true);
      setDeferredPrompt(null);
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border/50 px-4 py-3">
          <div className="flex items-center gap-3 max-w-2xl mx-auto">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-muted transition-colors">
              <ArrowLeft className="w-5 h-5 text-foreground" />
            </button>
            <h1 className="text-lg font-bold text-foreground">Installa Tecly</h1>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-10">
          {isInstalled ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <Download className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">App già installata!</h2>
              <p className="text-muted-foreground">Tecly è già sulla tua home screen.</p>
            </div>
          ) : (
            <div className="space-y-8">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-foreground mb-2">Installa l'app Tecly</h2>
                <p className="text-muted-foreground">
                  Aggiungi Tecly alla tua home screen per un'esperienza veloce e senza browser.
                </p>
              </div>

              {/* Android / Desktop con prompt nativo */}
              {deferredPrompt && (
                <div className="p-6 rounded-2xl bg-card border border-border/50 text-center">
                  <button
                    onClick={handleInstall}
                    className="px-8 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all active:scale-95"
                  >
                    <Download className="w-4 h-4 inline mr-2" />
                    Installa ora
                  </button>
                </div>
              )}

              {/* Istruzioni iOS */}
              {platform === "ios" && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FaApple className="w-6 h-6 text-foreground" />
                    <h3 className="text-lg font-semibold text-foreground">iPhone / iPad</h3>
                  </div>
                  <div className="space-y-3">
                    {[
                      { step: 1, icon: <Share className="w-5 h-5 text-primary" />, text: "Tocca il pulsante Condividi nella barra di Safari" },
                      { step: 2, icon: <Plus className="w-5 h-5 text-primary" />, text: "Scorri e seleziona \"Aggiungi alla schermata Home\"" },
                      { step: 3, icon: <Download className="w-5 h-5 text-primary" />, text: "Tocca \"Aggiungi\" in alto a destra" },
                    ].map((item) => (
                      <div key={item.step} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border/50">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {item.icon}
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground font-medium">Passo {item.step}</span>
                          <p className="text-sm text-foreground font-medium">{item.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Istruzioni Android */}
              {platform === "android" && !deferredPrompt && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FaAndroid className="w-6 h-6 text-foreground" />
                    <h3 className="text-lg font-semibold text-foreground">Android</h3>
                  </div>
                  <div className="space-y-3">
                    {[
                      { step: 1, icon: <MoreVertical className="w-5 h-5 text-primary" />, text: "Tocca il menu ⋮ in alto a destra nel browser" },
                      { step: 2, icon: <Plus className="w-5 h-5 text-primary" />, text: "Seleziona \"Aggiungi a schermata Home\"" },
                      { step: 3, icon: <Download className="w-5 h-5 text-primary" />, text: "Conferma toccando \"Aggiungi\"" },
                    ].map((item) => (
                      <div key={item.step} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border/50">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {item.icon}
                        </div>
                        <div>
                          <span className="text-xs text-muted-foreground font-medium">Passo {item.step}</span>
                          <p className="text-sm text-foreground font-medium">{item.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Desktop fallback */}
              {platform === "desktop" && !deferredPrompt && (
                <div className="p-6 rounded-2xl bg-card border border-border/50 text-center">
                  <p className="text-muted-foreground text-sm">
                    Apri questa pagina dal tuo smartphone per installare Tecly sulla home screen.
                  </p>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </PageTransition>
  );
};

export default Install;
